import { useState } from "react"
import styles from "./chat-page.module.css"

type FilterType = "all" | "private" | "group"
type ChatType = "private" | "group"

interface ChatContact {
  id: string
  name: string
  avatar?: string
  lastMessage: string
  time: string
  unread: number
  isOnline: boolean
  isGroup: boolean
  groupCount?: number
}

const mockChats: ChatContact[] = [
  {
    id: "1",
    name: "LangChain 交流群",
    lastMessage: "王老师：今天的作业大家记得按时提交",
    time: "刚刚",
    unread: 3,
    isOnline: true,
    isGroup: true,
    groupCount: 128,
  },
  {
    id: "2",
    name: "李明",
    avatar: "",
    lastMessage: "好的，我看看这个代码怎么改",
    time: "10:30",
    unread: 0,
    isOnline: true,
    isGroup: false,
  },
  {
    id: "3",
    name: "AI Agent 学习小组",
    lastMessage: "[张华]：有人知道 RAG 怎么优化吗？",
    time: "昨天",
    unread: 12,
    isOnline: false,
    isGroup: true,
    groupCount: 256,
  },
  {
    id: "4",
    name: "陈思",
    avatar: "",
    lastMessage: "这个项目很有前景，一起加油",
    time: "昨天",
    unread: 0,
    isOnline: false,
    isGroup: false,
  },
  {
    id: "5",
    name: "Python 机器学习班",
    lastMessage: "刘老师：下周开始学习深度学习章节",
    time: "周一",
    unread: 0,
    isOnline: false,
    isGroup: true,
    groupCount: 89,
  },
  {
    id: "6",
    name: "张伟",
    avatar: "",
    lastMessage: "代码 review 完成了，可以合并了",
    time: "周一",
    unread: 1,
    isOnline: true,
    isGroup: false,
  },
]

// SVG Icons
const MessageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const UsersGroupIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
    <circle cx="7" cy="10" r="2" />
    <path d="M16 8h3a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-3" />
    <circle cx="19" cy="15" r="2" />
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
)

// Filter Tab Component
const FilterTab = ({
  active,
  onClick,
  type,
  label,
}: {
  active: boolean
  onClick: () => void
  type: FilterType
  label: string
}) => {
  const icons = {
    all: <ChatIcon />,
    private: <UserIcon />,
    group: <UsersGroupIcon />,
  }

  return (
    <button
      className={`${styles.filterTab} ${active ? styles.activeFilterTab : ""}`}
      onClick={onClick}
    >
      {icons[type]}
      <span>{label}</span>
    </button>
  )
}

// Avatar Component
const Avatar = ({
  name,
  avatar,
  isOnline,
  isGroup,
}: {
  name: string
  avatar?: string
  isOnline: boolean
  isGroup: boolean
}) => {
  const initials = name.slice(0, 1)

  return (
    <div className={`${styles.avatar} ${!isOnline ? styles.avatarOffline : ""}`}>
      {avatar ? (
        <img src={avatar} alt={name} />
      ) : (
        <span className={styles.avatarFallback}>
          {isGroup ? <UsersIcon /> : initials}
        </span>
      )}
      {isOnline && <span className={styles.avatarBadge} />}
    </div>
  )
}

// Chat Item Component
const ChatItem = ({ chat }: { chat: ChatContact }) => {
  return (
    <div className={styles.chatItem}>
      <Avatar
        name={chat.name}
        avatar={chat.avatar}
        isOnline={chat.isOnline}
        isGroup={chat.isGroup}
      />
      <div className={styles.chatContent}>
        <div className={styles.chatHeader}>
          <span className={styles.chatName}>
            {chat.isGroup && <span className={styles.onlineDot} />}
            {chat.name}
          </span>
          <span className={styles.chatTime}>{chat.time}</span>
        </div>
        <div className={styles.chatMessage}>
          <span
            className={chat.unread > 0 ? styles.chatMessageUnread : ""}
          >
            {chat.lastMessage}
          </span>
          {chat.unread > 0 && (
            <span className={styles.unreadBadge}>{chat.unread}</span>
          )}
        </div>
      </div>
    </div>
  )
}

// Group Banner Component
const GroupBanner = () => (
  <div className={styles.groupBanner}>
    <div className={styles.groupBannerIcon}>
      <UsersGroupIcon />
    </div>
    <div className={styles.groupBannerInfo}>
      <div className={styles.groupBannerTitle}>创建学习小组</div>
      <div className={styles.groupBannerDesc}>与志同道合的伙伴一起成长</div>
    </div>
    <ChevronRightIcon />
  </div>
)

// Empty State Component
const EmptyState = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <MessageIcon />
    </div>
    <h3 className={styles.emptyTitle}>暂无消息</h3>
    <p className={styles.emptyDesc}>开始学习课程，与同学互动交流</p>
  </div>
)

export default function ChatPage() {
  const [activeFilter, setActiveFilter] = useState<FilterType>("all")
  const [searchQuery, setSearchQuery] = useState("")

  const filters: { key: FilterType; label: string }[] = [
    { key: "all", label: "全部" },
    { key: "private", label: "私聊" },
    { key: "group", label: "群聊" },
  ]

  const filteredChats = mockChats.filter((chat) => {
    if (activeFilter === "private") return !chat.isGroup
    if (activeFilter === "group") return chat.isGroup
    return true
  })

  const filteredBySearch = filteredChats.filter((chat) =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const groupChats = filteredBySearch.filter((c) => c.isGroup)
  const privateChats = filteredBySearch.filter((c) => !c.isGroup)

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerBg} />
        <div className={styles.headerContent}>
          <h1 className={styles.title}>
            <span className={styles.titleIcon}>
              <MessageIcon />
            </span>
            消息
          </h1>
        </div>
      </header>

      {/* Search */}
      <div className={styles.searchSection}>
        <div className={styles.searchWrapper}>
          <span className={styles.searchIcon}>
            <SearchIcon />
          </span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="搜索聊天记录..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Chat List */}
      <div className={styles.chatList}>
        {/* Filter Tabs */}
        <div className={styles.filterTabs}>
          {filters.map((filter) => (
            <FilterTab
              key={filter.key}
              active={activeFilter === filter.key}
              onClick={() => setActiveFilter(filter.key)}
              type={filter.key}
              label={filter.label}
            />
          ))}
        </div>

        {/* Group Banner */}
        {activeFilter !== "private" && (
          <>
            <GroupBanner />
            <h2 className={styles.sectionTitle}>
              <UsersGroupIcon />
              群聊 ({groupChats.length})
            </h2>
          </>
        )}

        {/* Group Chats */}
        {groupChats.map((chat) => (
          <ChatItem key={chat.id} chat={chat} />
        ))}

        {/* Private Chats */}
        {activeFilter !== "group" && (
          <>
            <h2 className={styles.sectionTitle}>
              <UserIcon />
              私聊 ({privateChats.length})
            </h2>
            {privateChats.map((chat) => (
              <ChatItem key={chat.id} chat={chat} />
            ))}
          </>
        )}

        {/* Empty State */}
        {filteredBySearch.length === 0 && <EmptyState />}
      </div>
    </div>
  )
}
